/** Settings → External Sources. */
import { useState } from "react";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ComingOnline, THead, SecretReveal } from "./shared";
import { useExternalSources, useCreateExternalSource, useDeleteExternalSource } from "@/hooks/security/useEdrAdmin";

const KINDS = [
  { value: "crowdstrike", label: "Crowdstrike Falcon" },
  { value: "jamf", label: "Jamf Pro" },
  { value: "okta", label: "Okta system log" },
  { value: "github", label: "GitHub audit log" },
  { value: "cloudtrail", label: "AWS CloudTrail" },
];

export function ExternalSourcesSection() {
  const q = useExternalSources();
  const [addOpen, setAddOpen] = useState(false);
  const unavail = q.data?.kind === "unavailable";
  const sources = q.data?.kind === "ok" ? q.data.data.sources : [];
  const del = useDeleteExternalSource();

  return (
    <Panel title={`External sources (${sources.length})`}
      actions={<Button size="sm" disabled={unavail} onClick={() => setAddOpen(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Add source</Button>}
      loading={q.isLoading}
      empty={!q.isLoading && (unavail || sources.length === 0)}
      emptyTitle={unavail ? "Coming online — Q3 2026" : "No external sources yet"}
      emptyDescription={
        unavail ? "External-source endpoints aren't live yet." :
        "Smplify can ingest events from Crowdstrike, Jamf, Okta, GitHub, and AWS CloudTrail."
      }
    >
      <div className="overflow-x-auto -mx-4 -my-4">
        <table className="w-full text-xs">
          <THead cols={["Name", "Kind", "Created", "Last event", ""]} />
          <tbody>
            {sources.map((s) => (
              <tr key={s.id} className="border-b border-border/60 hover:bg-muted/40">
                <td className="px-3 py-2 font-medium text-foreground">{s.name}</td>
                <td className="px-3 py-2 uppercase text-[10px] text-muted-foreground">{s.kind}</td>
                <td className="px-3 py-2 text-muted-foreground tabular-nums">{formatDistanceToNow(new Date(s.createdAt), { addSuffix: true })}</td>
                <td className="px-3 py-2 text-muted-foreground tabular-nums">{s.lastEventAt ? formatDistanceToNow(new Date(s.lastEventAt), { addSuffix: true }) : "never"}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { if (confirm(`Remove ${s.name}?`)) del.mutate(s.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AddSourceDialog open={addOpen} onOpenChange={setAddOpen} />
    </Panel>
  );
}

function AddSourceDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState(KINDS[0].value);
  const [token, setToken] = useState<string | null>(null);
  const create = useCreateExternalSource();
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setName(""); setToken(null); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{token ? "Source added" : "Add an external source"}</DialogTitle>
          <DialogDescription>{token ? "Copy this ingest token now — it's only shown once." : "Each source needs an ingest token; point the vendor's webhook at our endpoint."}</DialogDescription>
        </DialogHeader>
        {token ? (
          <div className="space-y-2 py-2"><SecretReveal value={token} /></div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Vendor</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}
        <DialogFooter>
          {token ? <Button onClick={() => onOpenChange(false)}>Done</Button> : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button disabled={!name || create.isPending} onClick={async () => {
                const r = await create.mutateAsync({ name, kind });
                setToken(r.token);
              }}>{create.isPending ? "Adding…" : "Add"}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
